import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel';
import { PayPalPlan, TIER_DESCRIPTIONS } from '../types';
import { useSubscriptionData } from '../hooks/useSubscriptionData';
import { useSubscriptionActions } from '../hooks/useSubscriptionActions';
import SubscriptionsShell from './SubscriptionsShell';
import PlanCard from './PlanCard';
import { usePermissions, type Feature } from '../../../contexts/PermissionContext';
import { getTierFeatures } from '../../../utils/tierFeatures';
import type { PermissionDisplayMetadata } from '../../../services/permissionService';

export default function SubscriptionsMobile() {
  const data = useSubscriptionData();
  const actions = useSubscriptionActions(data.hasPendingDowngrade, data.subscriptionStatus, data.fetchSubscriptionData);
  const { getPermissionMetadata } = usePermissions();
  
  // Local state for carousel APIs
  const [, setMonthlyCarouselApi] = useState<any>(null);
  const [, setAnnualCarouselApi] = useState<any>(null);

  const renderMonthlyPlans = ({
    activeTier,
    activeBillingCycle,
    hasPaidSubscription,
    hasRoleBasedAccess,
    hasPendingDowngrade,
    subscriptionStatus,
    unifiedPlans,
    tierLimits,
    paypalSdkLoaded,
    upgradeLoading,
    downgradeLoading,
    onUpgrade,
    onDowngrade,
    fetchSubscriptionData,
    hasPendingCancellationToFree,
  }: any) => {
    // Wait for subscription data to load before rendering carousel
    // This ensures startIndex is correct and prevents visual jump
    const isDataReady = !data.loading && data.subscriptionStatus !== null;
    
    if (!isDataReady) {
      return (
        <div className="block md:hidden w-full max-w-full flex items-center justify-center py-12">
          <span className="text-purple-500">Loading...</span>
        </div>
      );
    }

    // Mobile: Carousel layout - only render when data is ready
    return (
      <div className="block md:hidden w-full max-w-full">
        <Carousel
          opts={{
            align: "start",
            loop: true,
            startIndex: data.getInitialCarouselIndex,
          }}
          setApi={setMonthlyCarouselApi}
          className="w-full"
        >
          <CarouselContent className="-ml-2 md:-ml-4">
            {/* Free Tier - Always shown first */}
            {(() => {
              const freeLimits = tierLimits.get('free');
              const isActive = activeTier === 'free';
              
              return (
                <CarouselItem key="free" className="pl-3 pr-1 pt-2 pb-2 md:pl-4 md:pt-0 md:pb-0">
                  <Card className={`relative flex flex-col ${
                    isActive ? 'ring-2 ring-purple-500' : ''
                  } ${
                    hasPendingCancellationToFree ? 'ring-2 ring-red-500 border-red-500' : ''
                  } subscription-tier-card w-full`}>
                    {isActive && (
                      <Badge className="absolute top-2 right-2" variant="default">
                        Current
                      </Badge>
                    )}
                    {hasPendingCancellationToFree && (() => {
                      const effectiveDate = subscriptionStatus?.next_billing_date || subscriptionStatus?.subscription_end_date;
                      return (
                        <Badge className="absolute top-2 right-2 bg-red-500 text-white" variant="default">
                          {effectiveDate
                            ? `Starting ${new Date(effectiveDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                            : 'Starting Next Billing Cycle'}
                        </Badge>
                      );
                    })()}
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-xl">Free</CardTitle>
                      </div>
                      <CardDescription>
                        {TIER_DESCRIPTIONS.free}
                        {hasPendingCancellationToFree && (() => {
                          const effectiveDate = subscriptionStatus?.next_billing_date || subscriptionStatus?.subscription_end_date;
                          return effectiveDate ? (
                            <span className="block mt-1 text-red-600 dark:text-red-400">
                              Changes take effect on {new Date(effectiveDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                            </span>
                          ) : null;
                        })()}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col flex-grow overflow-visible">
                      <div className="mb-4">
                        <span className="text-3xl font-bold">$0</span>
                        <span className="text-muted-foreground"> per month</span>
                      </div>
                      <ul className="space-y-2 mb-4 text-sm flex-grow">
                        {getTierFeatures({
                          tier: 'free',
                          limits: freeLimits || null,
                          getPermissionMetadata: getPermissionMetadata as (feature: Feature) => PermissionDisplayMetadata | undefined,
                        }).map((feature, index) => (
                          <li key={index} className="flex items-center gap-2">
                            {!feature.isHeader && (
                              feature.included ? (
                                <span className="text-green-500">✓</span>
                              ) : (
                                <span className="text-red-500">✗</span>
                              )
                            )}
                            {feature.isHeader && <span className="flex-shrink-0" />}
                            <span className={feature.bold ? 'font-medium' : feature.included ? '' : 'text-muted-foreground'}>
                              {feature.text}
                            </span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-auto w-full" style={{ height: '120px', minHeight: '120px' }}></div>
                      <Button className="w-full" variant={isActive ? 'default' : 'outline'} disabled={true}>
                        {isActive ? 'Current Plan' : 'Not Available'}
                      </Button>
                    </CardContent>
                  </Card>
                </CarouselItem>
              );
            })()}

            {/* Dynamic monthly tiers from unified app plans */}
            {unifiedPlans.flatMap((unifiedPlan: any) => {
              const plans = Object.values(unifiedPlan.monthly_plans || {});
              return plans;
            })
            .filter((plan: PayPalPlan) => plan.tier !== 'free')
            .sort((a: PayPalPlan, b: PayPalPlan) => {
              const tierOrder: Record<string, number> = { starter: 1, pro: 2 };
              return (tierOrder[a.tier] || 999) - (tierOrder[b.tier] || 999);
            })
              .map((plan: PayPalPlan) => {
                const limits = tierLimits.get(plan.tier);
                const containerId = `mobile-paypal-button-container-${plan.paypal_plan_id}`;
                
                return (
                  <CarouselItem key={plan.id} className="pl-3 pr-1 pt-2 pb-2 md:pl-4 md:pt-0 md:pb-0">
                    <PlanCard
                    plan={plan}
                    limits={limits}
                    subscriptionStatus={subscriptionStatus}
                    activeTier={activeTier}
                    activeBillingCycle={activeBillingCycle}
                    hasPaidSubscription={hasPaidSubscription}
                    hasRoleBasedAccess={hasRoleBasedAccess}
                    hasPendingDowngrade={hasPendingDowngrade}
                    paypalSdkLoaded={paypalSdkLoaded}
                    upgradeLoading={upgradeLoading}
                    downgradeLoading={downgradeLoading}
                    containerId={containerId}
                    freeLimits={tierLimits.get('free')}
                    starterLimits={tierLimits.get('starter')}
                    onUpgrade={onUpgrade}
                    onDowngrade={onDowngrade}
                    onSubscriptionSuccess={fetchSubscriptionData}
                  />
                </CarouselItem>
              );
            })}
          </CarouselContent>
          <CarouselPrevious className="left-2" />
          <CarouselNext className="right-2" />
        </Carousel>
      </div>
    );
  };

  const renderAnnualPlans = ({
    activeTier,
    activeBillingCycle,
    hasPaidSubscription,
    hasRoleBasedAccess,
    hasPendingDowngrade,
    subscriptionStatus,
    unifiedPlans,
    tierLimits,
    paypalSdkLoaded,
    upgradeLoading,
    downgradeLoading,
    onUpgrade,
    onDowngrade,
    fetchSubscriptionData,
    hasPendingCancellationToFree,
  }: any) => {
    // Wait for subscription data to load before rendering carousel
    // This ensures startIndex is correct and prevents visual jump
    const isDataReady = !data.loading && data.subscriptionStatus !== null;
    
    if (!isDataReady) {
      return (
        <div className="block md:hidden w-full max-w-full flex items-center justify-center py-12">
          <span className="text-purple-500">Loading...</span>
        </div>
      );
    }

    // Mobile: Carousel layout - only render when data is ready
    const annualPlans = unifiedPlans.flatMap((unifiedPlan: any) => Object.values(unifiedPlan.annual_plans || {}));
    const seen = new Set<string>();
    const dedup: PayPalPlan[] = [];
    annualPlans.forEach((plan: PayPalPlan) => {
      if (!seen.has(plan.tier)) {
        seen.add(plan.tier);
        dedup.push(plan);
      }
    });

    return (
      <div className="block md:hidden w-full max-w-full">
        <Carousel
          opts={{
            align: "start",
            loop: true,
            startIndex: data.getInitialCarouselIndex,
          }}
          setApi={setAnnualCarouselApi}
          className="w-full"
        >
          <CarouselContent className="-ml-2 md:-ml-4">
            {/* Free Tier - Always shown first */}
            {(() => {
              const freeLimits = tierLimits.get('free');
              const isActive = activeTier === 'free';

              return (
                <CarouselItem key="free-annual" className="pl-3 pr-1 pt-2 pb-2 md:pl-4 md:pt-0 md:pb-0">
                  <Card className={`relative flex flex-col ${
                    isActive ? 'ring-2 ring-purple-500' : ''
                  } ${
                    hasPendingCancellationToFree ? 'ring-2 ring-red-500 border-red-500' : ''
                  } subscription-tier-card w-full`}>
                    {isActive && (
                      <Badge className="absolute top-2 right-2" variant="default">
                        Current
                      </Badge>
                    )}
                    {hasPendingCancellationToFree && (() => {
                      const effectiveDate = subscriptionStatus?.next_billing_date || subscriptionStatus?.subscription_end_date;
                      return (
                        <Badge className="absolute top-2 right-2 bg-red-500 text-white" variant="default">
                          {effectiveDate
                            ? `Starting ${new Date(effectiveDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                            : 'Starting Next Billing Cycle'}
                        </Badge>
                      );
                    })()}
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-xl">Free</CardTitle>
                      </div>
                      <CardDescription>{TIER_DESCRIPTIONS.free}</CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col flex-grow overflow-visible">
                      <div className="mb-4">
                        <span className="text-3xl font-bold">$0</span>
                        <span className="text-muted-foreground"> per year</span>
                      </div>
                      <ul className="space-y-2 mb-4 text-sm flex-grow">
                        {getTierFeatures({
                          tier: 'free',
                          limits: freeLimits || null,
                          getPermissionMetadata: getPermissionMetadata as (feature: Feature) => PermissionDisplayMetadata | undefined,
                        }).map((feature, index) => (
                          <li key={index} className="flex items-center gap-2">
                            {!feature.isHeader && (
                              feature.included ? (
                                <span className="text-green-500">✓</span>
                              ) : (
                                <span className="text-red-500">✗</span>
                              )
                            )}
                            {feature.isHeader && <span className="flex-shrink-0" />}
                            <span className={feature.bold ? 'font-medium' : feature.included ? '' : 'text-muted-foreground'}>
                              {feature.text}
                            </span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-auto w-full" style={{ height: '120px', minHeight: '120px' }}></div>
                      <Button className="w-full" variant={isActive ? 'default' : 'outline'} disabled={true}>
                        {isActive ? 'Current Plan' : 'Not Available'}
                      </Button>
                    </CardContent>
                  </Card>
                </CarouselItem>
              );
            })()}

            {dedup
              .filter(plan => plan.tier !== 'free')
              .sort((a: PayPalPlan, b: PayPalPlan) => {
                // Fixed order: Starter, Pro (Free is already shown first)
                const tierOrder: Record<string, number> = { starter: 1, pro: 2 };
                return (tierOrder[a.tier] || 999) - (tierOrder[b.tier] || 999);
              })
              .map((plan: PayPalPlan) => {
                const limits = tierLimits.get(plan.tier);
                const monthlyPlan = unifiedPlans
                  .flatMap((up: any) => Object.values(up.monthly_plans || {}))
                  .find((p: PayPalPlan) => p.tier === plan.tier);
                const containerId = `mobile-paypal-button-container-${plan.paypal_plan_id}`;
                
                return (
                  <CarouselItem key={plan.id} className="pl-3 pr-1 pt-2 pb-2 md:pl-4 md:pt-0 md:pb-0">
                      <PlanCard
                      plan={plan}
                      limits={limits}
                      subscriptionStatus={subscriptionStatus}
                      activeTier={activeTier}
                      activeBillingCycle={activeBillingCycle}
                      hasPaidSubscription={hasPaidSubscription}
                      hasRoleBasedAccess={hasRoleBasedAccess}
                      hasPendingDowngrade={hasPendingDowngrade}
                      paypalSdkLoaded={paypalSdkLoaded}
                      upgradeLoading={upgradeLoading}
                      downgradeLoading={downgradeLoading}
                      containerId={containerId}
                      monthlyPlan={monthlyPlan}
                      freeLimits={tierLimits.get('free')}
                      starterLimits={tierLimits.get('starter')}
                      onUpgrade={onUpgrade}
                      onDowngrade={onDowngrade}
                      onSubscriptionSuccess={fetchSubscriptionData}
                    />
                  </CarouselItem>
                );
              })}
          </CarouselContent>
          <CarouselPrevious className="left-2" />
          <CarouselNext className="right-2" />
        </Carousel>
      </div>
    );
  };

  return (
    <SubscriptionsShell
      data={data}
      actions={actions}
      renderMonthlyPlans={renderMonthlyPlans}
      renderAnnualPlans={renderAnnualPlans}
    />
  );
}
